import { useState, useEffect, useCallback } from "react";
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import Toaster from "./../../../components/common/Toaster";
import dashboardService from "../../../services/dashboardService";
import moment from "moment";

const colors = ["#808080", "#34D399", "#FCA5A5", "#60A5FA"];

const Financials = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [period] = useState({
    start_date: moment().subtract(6, "months").format("MM-DD-yyyy"),
    end_date: moment().format("MM-DD-yyyy"),
  });

  const fetchData = useCallback(
    async (mounted) => {
      try {
        setIsLoading(true);

        const projected_end = moment(period.end_date, "MM-DD-yyyy")
          .add(6, "months")
          .format("MM-DD-yyyy");

        const invoices_result =
          await dashboardService.getMonthlyInvoicesAmountAsync(
            period.start_date,
            period.end_date
          );

        const projected_result =
          await dashboardService.getMonthlyProjectedInvoicesAmountAsync(
            period.end_date,
            projected_end
          );

        const arrears_result =
          await dashboardService.getMonthlyArrearsAmountAsync(
            period.start_date,
            period.end_date
          );

        const collections_result =
          await dashboardService.getMonthlyPaymentsAmountAsync(
            period.start_date,
            period.end_date
          );

        const invoices = invoices_result.map((item) => {
          item["month"] = moment(item.start_date).format("MMM yyyy");
          item["total"] = item.total_amount;

          return item;
        });

        const projected = projected_result.map((item) => {
          item["month"] = moment(item.start_date).format("MMM yyyy");
          item["total"] = item.total_amount;

          return item;
        });

        const arrears = arrears_result.map((item) => {
          item["month"] = moment(item.start_date).format("MMM yyyy");
          item["total"] = item.total_amount;

          return item;
        });

        const collections = collections_result.map((item) => {
          item["month"] = moment(item.start_date).format("MMM yyyy");
          item["total"] = item.total_amount;

          return item;
        });

        if (mounted) {
          const series = [
            {
              name: "Invoices",
              data: invoices,
              is_line_graph: false,
            },
            {
              name: "Projected",
              data: projected,
              is_line_graph: false,
            },
            {
              name: "Arrears",
              data: arrears,
              is_line_graph: true,
            },
            {
              name: "Collections",
              data: collections,
              is_line_graph: true,
            },
          ];

          setData(series);
          setIsLoading(false);
        }
      } catch (ex) {
        Toaster.Error("Something went wrong.");
        setIsLoading(false);
      }
    },
    [period]
  );

  useEffect(() => {
    let mounted = true;

    fetchData(mounted);

    return () => {
      mounted = false;
    };
  }, [fetchData]);

  return (
    <div className="card p-6">
      <div className="flex">
        <p className="text-gray-500">Financials</p>
      </div>
      {isLoading && (
        <div className="flex justify-center items-center space-x-1 h-96">
          <div className="bg-blue-400 w-2 h-2 full rounded-full animate-bounce blue-circle"></div>
          <div className="bg-blue-400 w-2 h-2 full rounded-full animate-bounce blue-circle"></div>
          <div className="bg-blue-400 w-2 h-2 full rounded-full animate-bounce blue-circle"></div>
        </div>
      )}
      {!isLoading && (
        <ResponsiveContainer width="100%" height={450}>
          <ComposedChart
            width={500}
            height={500}
            data={data}
            margin={{
              top: 40,
              right: 40,
              left: 20,
              bottom: 20,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="month"
              type="category"
              strokeWidth={0.6}
              axisLine={false}
              tickMargin={15}
              tickLine={false}
              tickSize={5}
              allowDuplicatedCategory={false}
              tick={{ fill: "#9ca3af", fontSize: "0.875rem" }}
            />
            <YAxis
              allowDecimals={false}
              axisLine={false}
              tickMargin={15}
              tickLine={false}
              tickSize={5}
              tick={{ fill: "#9ca3af", fontSize: "0.875rem" }}
              tickFormatter={(value) => value.toLocaleString()}
            />
            <Tooltip formatter={(value) => value.toLocaleString()} />
            {data.map((item, key) => {
              if (item.is_line_graph) {
                return (
                  <Bar
                    dataKey="total"
                    data={item.data}
                    name={item.name}
                    key={key}
                    fill={colors[key]}
                    fillOpacity={0.5}
                    stroke={colors[key]}
                    strokeWidth={1.5}
                    barSize={30}
                    barCategoryGap={15}
                  />
                );
              }

              return (
                <Line
                  type="monotone"
                  dataKey="total"
                  data={item.data}
                  name={item.name}
                  key={item.name}
                  stroke={colors[key]}
                  strokeWidth={1.8}
                />
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default Financials;
